import { CardWrapper } from 'layout/PublicLayout/Wrappers';
import {
    Box,
    Stack,
    Grid,
    TablePagination,
    CircularProgress,
    Typography,
    Paper,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableSortLabel
} from '@mui/material';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { athlete, club } from 'apis';
import { Autocomplete, TextInput } from 'qubCommon/Inputs';
import { useForm, useWatch, FormProvider } from 'react-hook-form';
import React, { useState } from 'react';
import { debounce } from 'lodash';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

const fieldValueSx = {
    fontWeight: 700,
    color: '#315b9f',
    textAlign: 'right',
    overflowWrap: 'anywhere'
} as const;

const tableHeaderCellSx = {
    py: 1.25,
    px: 1.5,
    color: '#fff',
    fontWeight: 800,
    fontSize: '0.9rem',
    backgroundColor: '#7eaaca',
    borderBottom: 'none',
    whiteSpace: 'nowrap'
} as const;

const tableBodyCellSx = {
    py: 1.1,
    px: 1.5,
    fontSize: '0.92rem',
    borderBottom: 'none',
    overflowWrap: 'anywhere'
} as const;

const sortLabelSx = {
    color: '#fff !important',
    '&.Mui-active': {
        color: '#fff !important'
    },
    '& .MuiTableSortLabel-icon': {
        color: '#fff !important'
    }
} as const;

type SortDirection = 'asc' | 'desc';

const View = () => {
    const form = useForm();
    const organization = useWatch({
        control: form.control,
        name: 'Organization'
    });
    const [autocompleteValue, setAutoCompleteValue] = useState<string>('');
    const [search, setSearch] = useState<string>('');
    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(50);
    const [totalRows, setTotalRows] = useState<number | null>(null);
    const [orderBy, setOrderBy] = useState<string>('organization');
    const [order, setOrder] = useState<SortDirection>('asc');
    const { t } = useTranslation();
    const theme = useTheme();
    const mdUp = useMediaQuery(theme.breakpoints.up('md'));

    const handleChangePage = (_: any, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleRequestSort = (field: string) => {
        const isActiveColumn = orderBy === field;
        setOrder(isActiveColumn && order === 'asc' ? 'desc' : 'asc');
        setOrderBy(field);
        setPage(0);
    };

    const athletesQuery = useQuery({
        queryKey: ['athletes', organization, search, page, rowsPerPage, orderBy, order],
        queryFn: async () => {
            var res = await athlete.getPublicAthletes({
                organizationId: organization?.id,
                search: search,
                Page: page + 1,
                PageSize: rowsPerPage,
                SortField: orderBy,
                SortDirection: order
            });
            setTotalRows(res.count);
            return res;
        }
    });

    const organizationsQuery = useQuery({
        queryKey: ['organizations', autocompleteValue],
        queryFn: async () => {
            var res = await club.getClubsPublic({
                search: autocompleteValue
            });
            return res;
        }
    });

    const handleAutoCompleteChange = debounce(function (value: string) {
        setAutoCompleteValue(value);
    }, 500);

    const handleSearchChange = debounce(function (value: string) {
        setSearch(value);
    }, 500);

    const rows = athletesQuery?.data?.items || [];
    const renderSortLabel = (field: string, label: string) => (
        <TableSortLabel
            active={orderBy === field}
            direction={orderBy === field ? order : 'asc'}
            onClick={() => handleRequestSort(field)}
            sx={sortLabelSx}
        >
            {label}
        </TableSortLabel>
    );

    return (
        <CardWrapper sx={{ background: '#fff', py: { xs: 1.5, md: 2 }, px: { xs: 1.25, md: 2 } }}>
            <Stack spacing={{ xs: 1.5, md: 2 }} sx={{ width: '100%', minWidth: 0 }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={0.75}
                >
                    <Box>
                        <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.25rem', md: '1.5rem' }, lineHeight: 1.2 }}>
                            {t('Athletes')}
                        </Typography>
                        <Typography sx={{ color: '#667085', fontSize: { xs: '0.85rem', md: '0.92rem' } }}>
                            {totalRows !== null ? `${totalRows} ${t('Records')}` : t('Search')}
                        </Typography>
                    </Box>
                    {athletesQuery?.isFetching && <CircularProgress size={22} />}
                </Stack>

                <FormProvider {...form}>
                    <Box
                        sx={{
                            p: { xs: 1.25, md: 1.5 },
                            border: '1px solid #dde5ef',
                            borderRadius: 2,
                            background: '#f8fbff'
                        }}
                    >
                        <Grid container spacing={{ xs: 1.25, md: 1.5 }}>
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    label={t('Organization')}
                                    name="Organization"
                                    labelName="title"
                                    options={organizationsQuery.data || []}
                                    onInputChange={(_, value) => handleAutoCompleteChange(value)}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextInput
                                    label={t('Search')}
                                    name="Search"
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearchChange(e.target.value)}
                                    endAdornment={athletesQuery?.isLoading ? <CircularProgress size="18px" /> : null}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </FormProvider>

                {mdUp ? (
                    <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{ width: '100%', overflowX: 'auto', borderRadius: 2, border: '1px solid #d9e3ec' }}
                    >
                        <Table size="small" sx={{ minWidth: 900 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ ...tableHeaderCellSx, width: 110 }}>
                                        {renderSortLabel('regNumber', t('RegNumber'))}
                                    </TableCell>
                                    <TableCell sx={{ ...tableHeaderCellSx, minWidth: 230 }}>{renderSortLabel('fullName', t('Name'))}</TableCell>
                                    <TableCell sx={{ ...tableHeaderCellSx, width: 110 }}>{renderSortLabel('gender', t('Gender'))}</TableCell>
                                    <TableCell sx={{ ...tableHeaderCellSx, width: 110 }}>{renderSortLabel('dob', t('Yob'))}</TableCell>
                                    <TableCell sx={{ ...tableHeaderCellSx, minWidth: 260 }}>
                                        {renderSortLabel('organization', t('Organization'))}
                                    </TableCell>
                                    <TableCell sx={{ ...tableHeaderCellSx, width: 140 }}>
                                        {renderSortLabel('nationality', t('Nationality'))}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.length > 0 ? (
                                    rows.map((row: any, index: number) => (
                                        <TableRow
                                            key={`${row.regNumber}-${row.fullName}`}
                                            sx={{
                                                backgroundColor: index % 2 === 0 ? '#fff' : '#f0f3f6',
                                                '&:hover': { backgroundColor: '#d7e5f1' }
                                            }}
                                        >
                                            <TableCell sx={{ ...tableBodyCellSx, fontWeight: 800, color: '#315b9f' }}>
                                                {row.regNumber ?? '-'}
                                            </TableCell>
                                            <TableCell sx={{ ...tableBodyCellSx, fontWeight: 700 }}>{row.fullName ?? '-'}</TableCell>
                                            <TableCell sx={tableBodyCellSx}>{row.gender ?? '-'}</TableCell>
                                            <TableCell sx={tableBodyCellSx}>{row.dob ?? '-'}</TableCell>
                                            <TableCell sx={tableBodyCellSx}>{row.organization ?? '-'}</TableCell>
                                            <TableCell sx={tableBodyCellSx}>{row.nationality ?? '-'}</TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={6} sx={{ ...tableBodyCellSx, py: 3, textAlign: 'center', color: '#667085' }}>
                                            {athletesQuery?.isLoading ? t('Loading') : t('NoData')}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Stack spacing={1}>
                        {rows.length > 0 ? (
                            rows.map((row: any) => (
                                <Paper
                                    key={`${row.regNumber}-${row.fullName}`}
                                    elevation={0}
                                    sx={{ p: 1.25, border: '1px solid #d9e3ec', borderRadius: 2, background: '#fff' }}
                                >
                                    <Stack spacing={1}>
                                        <Stack direction="row" justifyContent="space-between" spacing={1}>
                                            <Typography sx={{ fontWeight: 800, color: '#315b9f' }}>{row.regNumber ?? '-'}</Typography>
                                            <Typography sx={{ color: '#667085', fontSize: '0.85rem' }}>{row.dob ?? '-'}</Typography>
                                        </Stack>
                                        <Typography sx={{ fontWeight: 800, fontSize: '1rem', lineHeight: 1.25, overflowWrap: 'anywhere' }}>
                                            {row.fullName ?? '-'}
                                        </Typography>
                                        <Grid container spacing={0.75}>
                                            <Grid item xs={5}>
                                                <Typography sx={{ color: '#667085', fontSize: '0.85rem' }}>{t('Gender')}</Typography>
                                            </Grid>
                                            <Grid item xs={7}>
                                                <Typography sx={fieldValueSx}>{row.gender ?? '-'}</Typography>
                                            </Grid>
                                            <Grid item xs={5}>
                                                <Typography sx={{ color: '#667085', fontSize: '0.85rem' }}>{t('Organization')}</Typography>
                                            </Grid>
                                            <Grid item xs={7}>
                                                <Typography sx={fieldValueSx}>{row.organization ?? '-'}</Typography>
                                            </Grid>
                                            <Grid item xs={5}>
                                                <Typography sx={{ color: '#667085', fontSize: '0.85rem' }}>{t('Nationality')}</Typography>
                                            </Grid>
                                            <Grid item xs={7}>
                                                <Typography sx={fieldValueSx}>{row.nationality ?? '-'}</Typography>
                                            </Grid>
                                        </Grid>
                                    </Stack>
                                </Paper>
                            ))
                        ) : (
                            <Paper elevation={0} sx={{ p: 2, border: '1px dashed #cfd8e3', borderRadius: 2, textAlign: 'center' }}>
                                <Typography sx={{ color: '#667085' }}>{athletesQuery?.isLoading ? t('Loading') : t('NoData')}</Typography>
                            </Paper>
                        )}
                    </Stack>
                )}

                {totalRows && (
                    <Box sx={{ overflowX: 'auto' }}>
                        <TablePagination
                            component="div"
                            count={totalRows}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            sx={{ minWidth: { xs: 330, sm: 'auto' } }}
                        />
                    </Box>
                )}
            </Stack>
        </CardWrapper>
    );
};

export default View;
